import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SEO from '../SEO/SEO';

import { FiSearch, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { MdOutlineFormatListBulleted } from "react-icons/md";
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

export default function Faq() {
  const [open, setOpen] = useState(null);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("All");
  const [faqData, setFaqData] = useState([]);
  const [rates, setRates] = useState([]);
  const [deviations, setDeviations] = useState([]);
  const [banner, setBanner] = useState(null);
  const [settings, setSettings] = useState({
    section_title: 'Frequently Asked Questions',
    section_subtitle: 'Find answers to common questions about our services'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [faqsRes, settingsRes, bannerRes, ratesRes, deviationsRes] = await Promise.all([
          axios.get('/api/faqs/general/active'),
          axios.get('/api/faqs/settings/general'),
          axios.get('/api/banners/active?page_type=faq_page'),
          axios.get('/api/rate-matrix/active'),
          axios.get('/api/faq-deviations/active')
        ]);

        if (faqsRes.data.success) {
          setFaqData(faqsRes.data.data);
        }

        if (settingsRes.data.success && settingsRes.data.data) {
          setSettings(settingsRes.data.data);
        }

        if (bannerRes.data.success && bannerRes.data.data && bannerRes.data.data.length > 0) {
          setBanner(bannerRes.data.data[0]);
        }

        if (ratesRes.data.success) {
          setRates(ratesRes.data.data);
        }

        if (deviationsRes.data.success) {
          setDeviations(deviationsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching FAQ data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(faqData.map(f => f.category || f.cat)))], [faqData]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return faqData.filter(f => {
      const category = f.category || f.cat;
      if (activeCat !== "All" && category !== activeCat) return false;
      if (!q) return true;
      return f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q);
    });
  }, [query, activeCat, faqData]);
  
  
  // Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "image": "https://ahamhfc.com/img/loan-banners/Slide-02-1770722226693-126687268.webp?t=1770802805000",
        "description": "AHAM HOUSING FINANCE LIMITED offers home loans across multiple customer segments and regions in India.",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/home-loan-faq/#webpage",
        "url": "https://ahamhfc.com/home-loan-faq",
        "name": "Home Loan FAQ | Aham Housing Finance",
        "description": "Find answers to common home loan questions from Aham Housing Finance.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "@id": "https://ahamhfc.com/home-loan-faq/#primaryimage",
          "contentUrl": "https://ahamhfc.com/img/loan-banners/Slide-02-1770722226693-126687268.webp?t=1770802805000"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "FAQPage",
        "@id": "https://ahamhfc.com/home-loan-faq/#faqpage",
        "url": "https://ahamhfc.com/home-loan-faq",
        "name": "Home Loan FAQ | Aham Housing Finance",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Can I get a tax benefit on my Home Loan?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes — tax benefits on principal & interest are available under Section 80C & 24(b) of the Income Tax Act, subject to the policy at the time."
            }
          },
          {
            "@type": "Question",
            "name": "Can I pre-close my loan?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Pre-closure is allowed as per loan agreement. Prepayment charges (if any) depend on your loan terms."
            }
          },
          {
            "@type": "Question",
            "name": "How is interest calculated on my loan?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Interest is calculated monthly based on the outstanding principal and the effective rate applicable to your loan slab."
            }
          },
          {
            "@type": "Question",
            "name": "How long will it take to sanction my loan?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Typical processing time is 3–7 business days once docs & verification are complete; complex cases may take longer."
            }
          },
          {
            "@type": "Question",
            "name": "What documents are needed for resale loan?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "KYC, income proof, property documents, sale agreement, EC, parent deeds (if required) and latest utility/tax receipts."
            }
          },
          {
            "@type": "Question",
            "name": "Who can apply for a resale loan?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Salaried and self-employed individuals meeting age, income and credit criteria may apply."
            }
          }
        ]
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://ahamhfc.com/home-loan-faq/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://ahamhfc.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "FAQ",
            "item": "https://ahamhfc.com/home-loan-faq"
          }
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-900 font-sans">

      <SEO
        title="Home Loan FAQs | Aham Housing Finance"
        description="Find answers to common home loan questions on eligibility, EMI, interest, tax benefits, pre-closure, and loans without income proof at Aham Housing Finance."
        keywords="Aham Housing Finance FAQ, home loan FAQ, home loan questions, housing finance FAQ, EMI questions, home loan interest questions, tax benefit on home loan, pre-closure of home loan, home loan eligibility FAQ, home loans without income proof"
        canonical="https://ahamhfc.com/home-loan-faq"
        ogTitle="Home Loan FAQs | Aham Housing Finance"
         schema={faqSchema}
        ogDescription="Find answers to common home loan questions on eligibility, EMI, interest, tax benefits, pre-closure, and loans without income proof at Aham Housing Finance."
      />


      {/* HERO */}
      <header className="relative min-h-[60vh] flex items-center justify-start overflow-hidden">
        {loading ? (
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="animate-pulse">
                <div className="h-8 w-48 bg-white/20 rounded mx-auto mb-4"></div>
                <div className="h-4 w-64 bg-white/10 rounded mx-auto"></div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
              style={{ backgroundImage: `url('${banner?.image_url || '/img/faq/faq.webp'}')` }}
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#02122f]/90 via-[#09306a]/70 to-transparent" />
            
            <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-4xl"
              >
                {/* Breadcrumb Navigation */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  className="flex items-center space-x-2 mb-6"
                >
                  <Link
                    to="/"
                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
                  >
                    Home
                  </Link>
                  <ChevronRightIcon className="h-4 w-4 text-white/60" />
                  <span className="text-yellow-300 text-sm font-semibold">FAQ</span>
                </motion.div>

                <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-tight drop-shadow-2xl">
                  {banner?.title || settings.section_title}
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed font-medium">
                  {banner?.subtitle || settings.section_subtitle}
                </p>
              </motion.div>
            </div>
          </>
        )}
      </header>

      {/* SEARCH FILTER */}
      <section className="max-w-7xl mx-auto px-6 -mt-14 relative z-20">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl border border-slate-200 p-6 md:p-8 flex flex-col md:flex-row gap-4 items-center font-sans">
          <div className="flex-1">
            <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-2 border border-slate-100">
              <FiSearch className="text-slate-400 text-xl ml-2" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search FAQs, e.g. 'tax benefit', 'documents', 'pre-close'..."
                className="w-full bg-transparent outline-none px-2 py-3 text-slate-900 placeholder:text-slate-400 font-medium"
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-3">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCat(cat)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition ${activeCat === cat
                    ? "bg-gradient-to-r from-yellow-400 to-yellow-300 text-slate-900 shadow"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="w-full md:w-56 text-right">
            <div className="text-sm text-slate-500">Need more help?</div>
            <a href="/contact-aham-housing-finance" className="inline-block mt-2 px-4 py-2 bg-[#ed2636] hover:bg-[#1f2654] text-white rounded-lg shadow hover:opacity-95 font-medium">
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <main className="max-w-7xl mx-auto px-6 mt-12 space-y-16 font-sans">

        {/* 1️⃣ FAQ Section */}
        <section>
          <h2 className="text-3xl font-bold text-slate-900 mb-6">{settings.section_title}</h2>
          <div className="space-y-4">
            {loading ? (
              <div className="grid gap-4">
                {[1, 2, 3].map(i => <div key={i} className="h-20 bg-slate-100 animate-pulse rounded-xl" />)}
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-6 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg text-yellow-800">
                No results found. Try a different keyword or view all categories.
              </div>
            ) : (
              filtered.map((f, i) => (
                <motion.div
                  key={f.id || i}
                  layout
                  className="bg-white shadow-sm rounded-xl overflow-hidden border border-slate-100"
                >
                  <button
                    onClick={() => setOpen(open === i ? null : i)}
                    className="w-full text-left p-5 flex items-start justify-between gap-4 hover:bg-slate-50"
                  >
                    <div>
                      <div className="text-base font-semibold text-slate-900">{f.question}</div>
                      <div className="text-sm text-slate-500 mt-1">{f.category || f.cat}</div>
                    </div>
                    <div className="text-slate-500 mt-1">
                      {open === i ? <FiChevronUp size={22} /> : <FiChevronDown size={22} />}
                    </div>
                  </button>

                  <AnimatePresence initial={false}>
                    {open === i && (
                      <motion.div
                        key="content"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35 }}
                        className="px-5 pb-5 pt-0 text-slate-700 bg-slate-50 leading-relaxed"
                      >
                        {f.answer}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))
            )}
          </div>
        </section>

        {/* 2️⃣ Rate Matrix Section */}
        <section>
          <div className="bg-white rounded-xl shadow border border-slate-100 p-6 font-sans">
            <div className="flex items-center gap-3 mb-4">
              <MdOutlineFormatListBulleted className="text-2xl text-[#0b4aa8]" />
              <h3 className="text-2xl font-bold text-slate-900">Rate Matrix</h3>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-[520px] w-full border border-slate-200 rounded-lg text-base text-slate-800">
                <thead>
                  <tr className="bg-[#0B3A63] text-white font-semibold">
                    <th className="py-3 px-3 text-left rounded-tl-lg">Category</th>
                    <th className="py-3 px-3 text-center">Superior</th>
                    <th className="py-3 px-3 text-center">Very Good</th>
                    <th className="py-3 px-3 text-center">Good</th>
                    <th className="py-3 px-3 text-center rounded-tr-lg">Medium</th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map((r, idx) => (
                    <tr
                      key={r.id || idx}
                      className={`${idx % 2 === 0 ? "bg-white" : "bg-slate-50"} hover:bg-slate-100 transition`}
                    >
                      <td className="py-3 px-3 font-medium">{r.category}</td>
                      <td className="py-3 px-3 text-center">{r.superior}</td>
                      <td className="py-3 px-3 text-center">{r.very_good}</td>
                      <td className="py-3 px-3 text-center">{r.good}</td>
                      <td className="py-3 px-3 text-center">{r.medium}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="mt-4 text-sm text-slate-600">
              <strong>Note:</strong> Rates are indicative and may vary based on credit assessment.
            </p>
          </div>
        </section>

        {/* 3️⃣ Deviations Section */}
        <section>
          <div className="bg-white rounded-xl shadow border border-slate-100 p-6 font-sans">
            <h4 className="text-2xl font-bold text-slate-900 mb-4">Deviations</h4>
            <ul className="text-slate-700 list-disc pl-6 space-y-2 text-base leading-relaxed">
              {deviations.map((d, index) => (
                <li key={d.id || index}>{d.content}</li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      <footer className="mt-20 pb-12 text-center text-sm text-slate-500">

      </footer>
    </div>
  );
}
